import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sprout, TrendingUp, Zap, Landmark, Microscope, PiggyBank } from 'lucide-react';
import SeoMeta from './SeoMeta';

interface Broker {
  id: string;
  name: string;
  initials: string;
  color: string;
  rating: number;
  verdict: string;
  keyStat: string;
  affiliateLink: string;
  pros: string[];
  cons: string[];
  bestFor: string;
  table: {
    accountOpeningFee: string;
    amc: string;
    equityDelivery: string;
    intraday: string;
    fAndO: string;
    threeInOne: boolean;
    appRating: string;
    support: string;
    aiResearch: string;
    ourVerdict: string;
  };
}

const brokers: Broker[] = [
  {
    id: 'zerodha', name: 'Zerodha', initials: 'Z', color: 'bg-blue-600',
    rating: 4.8, verdict: 'Best overall for active traders', keyStat: '₹20 flat brokerage',
    affiliateLink: 'https://zerodha.com/open-account?c=JA0171',
    pros: ['Industry-leading Kite platform with advanced charting', 'Largest user base in India — most trusted brand', 'Transparent fee structure, no hidden charges'],
    cons: ['No 3-in-1 account (no banking integration)', 'Customer support can be slow during peak market hours'],
    bestFor: 'Active traders and experienced investors who want the best trading platform',
    table: { accountOpeningFee: 'Free', amc: '₹300/year', equityDelivery: '0% (Free)', intraday: '0.03% or ₹20', fAndO: '₹20/order', threeInOne: false, appRating: '4.4 / 5', support: 'Ticket-based', aiResearch: 'Smallcase, Streak', ourVerdict: '#1 Overall Pick' }
  },
  {
    id: 'upstox', name: 'Upstox', initials: 'U', color: 'bg-purple-600',
    rating: 4.6, verdict: 'Best for beginners + lowest charges', keyStat: '₹20 flat brokerage',
    affiliateLink: 'https://upstox.onelink.me/0H1s/KY1170',
    pros: ['Instant account opening — fully paperless in 10 minutes', 'Clean, beginner-friendly mobile app', 'Backed by Tiger Global — strong financials'],
    cons: ['Research tools less comprehensive than Zerodha', 'AMC slightly higher for some account types'],
    bestFor: 'First-time investors and beginners who want a simple, low-cost experience',
    table: { accountOpeningFee: 'Free', amc: '₹249/year', equityDelivery: '0% (Free)', intraday: '0.05% or ₹20', fAndO: '₹20/order', threeInOne: false, appRating: '4.3 / 5', support: 'Chat + Call', aiResearch: 'Basic AI insights', ourVerdict: 'Best for Beginners' }
  },
  {
    id: 'groww', name: 'Groww', initials: 'G', color: 'bg-green-600',
    rating: 4.5, verdict: 'Best UI, great for mutual funds too', keyStat: '₹20 flat brokerage',
    affiliateLink: 'https://groww.in/open-demat-account?ref=REPLACE_YOUR_GROWW_REFERRAL_CODE',
    pros: ['Best-in-class mobile UI — most intuitive of all brokers', 'Seamless mutual fund + stock investing in one app', 'Perfect for SIP and long-term wealth building'],
    cons: ['Limited advanced charting for active traders', 'No F&O analytics tools'],
    bestFor: 'Investors who want a clean, all-in-one app for stocks and mutual funds',
    table: { accountOpeningFee: 'Free', amc: '₹0 (Free)', equityDelivery: '0% (Free)', intraday: '0.05% or ₹20', fAndO: '₹20/order', threeInOne: false, appRating: '4.5 / 5', support: 'In-app Chat', aiResearch: 'Basic insights', ourVerdict: 'Best UI & MF Platform' }
  },
  {
    id: 'angelone', name: 'Angel One', initials: 'A', color: 'bg-orange-600',
    rating: 4.3, verdict: 'Best for research + SmartAPI', keyStat: '₹0 for delivery',
    affiliateLink: 'https://angel-one.onelink.me/Wjgr/ahr63txg',
    pros: ['ARQ Prime AI-powered research for stock recommendations', 'SmartAPI for algo traders and developers', '3-in-1 account available (banking + demat + trading)'],
    cons: ['Older UI — less polished than Groww or Upstox', 'Some charges for margin trading facility'],
    bestFor: 'Research-driven investors and algo traders who need API access',
    table: { accountOpeningFee: 'Free', amc: '₹240/year', equityDelivery: '0% (Free)', intraday: '0.03% or ₹20', fAndO: '₹20/order', threeInOne: true, appRating: '4.2 / 5', support: 'Call + Chat', aiResearch: 'ARQ Prime AI', ourVerdict: 'Best for Research' }
  },
  {
    id: 'fyers', name: 'Fyers', initials: 'F', color: 'bg-red-600',
    rating: 4.2, verdict: 'Best for F&O and options traders', keyStat: '₹20 flat brokerage',
    affiliateLink: 'https://open-account.fyers.in/?utm_source=REPLACE_YOUR_FYERS_CODE',
    pros: ['Best-in-class options analytics with Options Hub', 'Fast order execution — critical for F&O traders', 'Excellent charting via TradingView integration'],
    cons: ['Smaller user base — fewer community resources', 'Not ideal for beginners or long-term investors'],
    bestFor: 'Derivatives and options traders who need advanced analytics',
    table: { accountOpeningFee: 'Free', amc: '₹300/year', equityDelivery: '0% (Free)', intraday: '0.03% or ₹20', fAndO: '₹20/order', threeInOne: false, appRating: '4.1 / 5', support: 'Ticket + Chat', aiResearch: 'Options Hub', ourVerdict: 'Best for F&O Trading' }
  }
];

const faqs = [
  { q: 'What is a demat account?', a: 'A demat (dematerialised) account holds your shares, bonds, ETFs, and mutual fund units in electronic form — replacing old physical share certificates. Every investor who wants to buy or sell stocks on NSE or BSE in India must have a demat account linked to a trading account.' },
  { q: 'Is it free to open a demat account in India?', a: 'Yes. As of 2026, all major brokers — Zerodha, Upstox, Groww, Angel One, and Fyers — offer completely free account opening online. The process is 100% paperless and takes 10–30 minutes with Aadhaar-based e-KYC.' },
  { q: 'Which demat account has zero brokerage?', a: 'All five brokers reviewed here charge zero brokerage on equity delivery (buying and holding stocks). For intraday and F&O trades, they charge a flat ₹20 per order or a small percentage — whichever is lower. Groww also charges ₹0 AMC (annual maintenance charge).' },
  { q: 'What documents are needed to open a demat account?', a: "You need: (1) PAN card — mandatory for all Indian investors, (2) Aadhaar card for e-KYC and address proof, (3) a bank account for fund transfers, and (4) a selfie or live photo for identity verification." },
  { q: 'Can I have multiple demat accounts?', a: "Yes, SEBI allows you to have multiple demat accounts with different brokers. Many investors maintain two accounts — one with Zerodha for active trading and one with Groww for mutual funds and SIPs. Each account has its own AMC, so factor in the annual cost." },
  { q: 'Which is better — Zerodha or Upstox?', a: "For active traders, Zerodha wins due to its superior Kite platform, larger ecosystem, and trusted brand. For beginners, Upstox has a slight edge with a simpler UI and lower AMC. Both charge ₹20 flat for intraday and F&O trades." },
  { q: 'Is Groww safe for long-term investing?', a: "Yes. Groww is SEBI-registered, a member of NSE and BSE, and backed by institutional investors. Your securities are held with NSDL/CDSL depositories — not with Groww directly — so they are safe even if the broker faces issues." },
  { q: 'What is the best demat account for beginners?', a: 'We recommend Groww or Upstox for beginners. Groww has the most intuitive UI and makes it easy to invest in both stocks and mutual funds from one app. Both are free to open and have zero delivery brokerage.' },
  { q: 'How long does it take to open a demat account online?', a: "With Aadhaar-based e-KYC, you can open a demat account in as little as 10 minutes. After submission, activation typically takes a few hours to 1 business day." },
  { q: 'Do I need a demat account to invest in mutual funds?', a: "No — you do not need a demat account to invest in direct mutual funds via AMC websites. However, if you want to invest in ETFs or mutual funds through a broker's app, a demat account is required." }
];

function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const partial = rating - full;
  const empty = 5 - full - (partial > 0 ? 1 : 0);
  return (
    <span className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: full }).map((_, i) => (
        <svg key={`f-${i}`} className="w-4 h-4 text-navy-500 fill-current" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.064 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.285-3.957z" />
        </svg>
      ))}
      {partial > 0 && (
        <svg className="w-4 h-4 text-navy-500" viewBox="0 0 20 20" aria-hidden="true">
          <defs><linearGradient id={`partial-${rating}`}><stop offset={`${partial * 100}%`} stopColor="currentColor" /><stop offset={`${partial * 100}%`} stopColor="transparent" /></linearGradient></defs>
          <path fill={`url(#partial-${rating})`} stroke="currentColor" strokeWidth="0.5" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.064 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.285-3.957z" />
        </svg>
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <svg key={`e-${i}`} className="w-4 h-4 text-navy-300" viewBox="0 0 20 20" aria-hidden="true">
          <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.064 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.285-3.957z" />
        </svg>
      ))}
      <span className="ml-1 text-sm font-semibold text-navy-500">{rating}</span>
    </span>
  );
}

function AffiliateButton({ broker, href, size = 'default' }: { broker: string; href: string; size?: 'default' | 'sm' }) {
  const sizing = size === 'sm' ? 'px-4 py-2 text-sm' : 'px-6 py-3 text-base';
  return (
    <a
      href={href}
      data-broker={broker}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={`inline-flex items-center justify-center font-bold rounded-xl transition-colors duration-150 bg-navy-500 hover:bg-navy-600 text-white ${sizing}`}
    >
      Open Free Account &rarr;
    </a>
  );
}

function FaqItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-start justify-between gap-4 px-6 py-5 bg-slate-50 hover:bg-slate-100 transition-colors duration-150"
        aria-expanded={open}
        aria-controls={`faq-answer-${index}`}
      >
        <span className="font-semibold text-slate-800 text-sm leading-snug">{q}</span>
        <span className={`flex-shrink-0 text-navy-500 text-xl leading-none transition-transform duration-200 ${open ? 'rotate-45' : ''}`} aria-hidden="true">+</span>
      </button>
      {open && (
        <div id={`faq-answer-${index}`} className="px-6 py-5 bg-white text-slate-600 text-sm leading-relaxed border-t border-slate-100">{a}</div>
      )}
    </div>
  );
}

const breadcrumbSchema = {
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://indianstockmarketgpt.com/' },
    { '@type': 'ListItem', position: 2, name: 'Best Demat Account India 2026', item: 'https://indianstockmarketgpt.com/best-demat-account-india' }
  ]
};

const faqSchema = {
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

const DematPage: React.FC = () => {
  return (
    <>
      <SeoMeta
        title="Best Demat Account in India 2026 — Full Comparison | IndianStockMarketGPT"
        description="Compare the 5 best demat accounts in India for 2026 — Zerodha, Upstox, Groww, Angel One & Fyers. Zero brokerage, charges, features & our honest verdict."
        keywords="best demat account india, zerodha vs upstox, best broker india 2026, free demat account india, zero brokerage demat"
        url="https://indianstockmarketgpt.com/best-demat-account-india"
        type="article"
        siteName="IndianStockMarketGPT"
      >
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </SeoMeta>

      <div className="bg-white text-slate-900">
        {/* Hero */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-100">
          <div className="max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-slate-400">
                <li><Link to="/" className="hover:text-navy-500 transition-colors">Home</Link></li>
                <li aria-hidden="true" className="text-slate-300">/</li>
                <li className="text-slate-600">Best Demat Account India 2026</li>
              </ol>
            </nav>

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-navy-50 border border-navy-100 rounded-full px-4 py-1.5 mb-5">
                <span className="w-2 h-2 rounded-full bg-navy-500 animate-pulse" aria-hidden="true" />
                <span className="text-navy-600 text-xs font-semibold tracking-wide uppercase">Updated April 2026</span>
              </div>

              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
                Best Demat Account in India (2026) —{' '}
                <span className="text-navy-500">Honest AI-Powered Comparison</span>
              </h1>

              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We analysed 5 top brokers across 12 parameters so you don't have to. Updated April 2026.
                Also check our{' '}
                <Link to="/blog/understanding-stock-market-basics-complete-beginners-guide" className="text-navy-500 hover:text-navy-700 underline underline-offset-2 transition-colors">stock market basics guide</Link>{' '}
                before opening your first account, or{' '}
                <Link to="/ask" className="text-navy-500 hover:text-navy-700 underline underline-offset-2 transition-colors">try our free AI stock analysis</Link>.
              </p>

              <div className="flex flex-wrap gap-3 mb-6" role="list" aria-label="Trust indicators">
                {['Zero bias', 'Updated April 2026', '12 parameters compared', 'AI-verified data'].map((badge) => (
                  <span key={badge} role="listitem" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-600 text-sm px-3 py-1.5 rounded-full">
                    <span className="text-green-500" aria-hidden="true">✓</span>
                    {badge}
                  </span>
                ))}
              </div>

              <p className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-4 py-3 leading-relaxed">
                <strong className="text-slate-700">Disclosure:</strong> This page contains affiliate links. We earn a small commission if you open an account through our links — at no additional cost to you. Our analysis is independent and unbiased.
              </p>
              <p className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-4 py-3 mt-3 leading-relaxed">
                <strong className="text-slate-700">How we rate:</strong> Ratings are editorial scores (out of 5) based on publicly available brokerage charges, app store ratings, SEBI registration data, and community feedback collected as of April 2026. They are not SEBI-certified assessments and are not investment advice.
              </p>
            </div>
          </div>
        </section>

        {/* Quick Verdict Cards */}
        <section className="py-12 px-4 sm:px-6 lg:px-8" aria-labelledby="quick-verdict-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="quick-verdict-heading" className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Quick Verdict — 2026 Rankings</h2>
            <p className="text-slate-500 text-sm mb-8">Scroll down for full analysis, comparison table, and detailed reviews.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {brokers.map((broker, idx) => (
                <article
                  key={broker.id}
                  className={`relative bg-white border rounded-2xl p-5 flex flex-col gap-4 transition-transform duration-200 hover:-translate-y-1 ${
                    broker.id === 'zerodha' ? 'border-navy-300 ring-1 ring-navy-100' : 'border-slate-200'
                  }`}
                >
                  {broker.id === 'zerodha' && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-navy-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">#1 Our Top Pick</span>
                  )}
                  <div className="flex items-center gap-3">
                    <div className={`${broker.color} w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0`} aria-hidden="true">{broker.initials}</div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-base">{broker.name}</h3>
                      <span className="text-slate-400 text-xs">#{idx + 1} ranked</span>
                    </div>
                  </div>
                  <p className="text-slate-600 text-xs leading-snug flex-1">{broker.verdict}</p>
                  <div className="bg-navy-50 border border-navy-100 rounded-lg px-3 py-2">
                    <span className="text-navy-600 font-bold text-sm">{broker.keyStat}</span>
                  </div>
                  <StarRating rating={broker.rating} />
                  <AffiliateButton broker={broker.id} href={broker.affiliateLink} size="sm" />
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-100" aria-labelledby="comparison-table-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="comparison-table-heading" className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Full Comparison Table (12 Parameters)</h2>
            <p className="text-slate-500 text-sm mb-8">All data verified as of April 2026. Broker charges are subject to change — always verify on the official website.</p>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
              <table className="w-full min-w-[800px] text-sm">
                <caption className="sr-only">Demat account comparison table for top 5 Indian brokers in 2026</caption>
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-200">
                    <th scope="col" className="text-left px-5 py-4 text-slate-600 font-semibold w-44">Parameter</th>
                    {brokers.map((b) => (
                      <th key={b.id} scope="col" className={`px-5 py-4 text-center font-bold ${b.id === 'zerodha' ? 'text-navy-600 bg-navy-50/50' : 'text-slate-800'}`}>
                        <div className="flex flex-col items-center gap-1">
                          <div className={`${b.color} w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-xs`} aria-hidden="true">{b.initials}</div>
                          {b.name}
                          {b.id === 'zerodha' && <span className="text-xs bg-navy-100 text-navy-600 px-2 py-0.5 rounded-full">Top Pick</span>}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {([
                    { label: 'Account Opening Fee', key: 'accountOpeningFee' },
                    { label: 'Annual Maintenance (AMC)', key: 'amc' },
                    { label: 'Equity Delivery Brokerage', key: 'equityDelivery' },
                    { label: 'Intraday Brokerage', key: 'intraday' },
                    { label: 'F&O Brokerage', key: 'fAndO' },
                    { label: '3-in-1 Account', key: 'threeInOne' },
                    { label: 'Mobile App Rating', key: 'appRating' },
                    { label: 'Customer Support', key: 'support' },
                    { label: 'AI / Research Tools', key: 'aiResearch' },
                    { label: 'Our Verdict', key: 'ourVerdict' },
                  ] as { label: string; key: keyof Broker['table'] }[]).map(({ label, key }, rowIdx) => (
                    <tr key={key} className={`${rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-slate-50 transition-colors`}>
                      <td className="px-5 py-3.5 text-slate-600 font-medium">{label}</td>
                      {brokers.map((b) => {
                        const val = b.table[key];
                        return (
                          <td key={b.id} className={`px-5 py-3.5 text-center ${b.id === 'zerodha' ? 'bg-navy-50/30' : ''} ${key === 'ourVerdict' ? 'text-navy-600 font-semibold text-xs' : 'text-slate-700'}`}>
                            {typeof val === 'boolean'
                              ? val
                                ? <span className="text-green-600 font-bold">✓</span>
                                : <span className="text-slate-400 font-bold">✗</span>
                              : val
                            }
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-3">* Charges are indicative. Verify current rates on each broker's official website before opening an account.</p>
          </div>
        </section>

        {/* Detailed Reviews */}
        <section className="py-12 px-4 sm:px-6 lg:px-8" aria-labelledby="reviews-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="reviews-heading" className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Detailed Broker Reviews</h2>
            <p className="text-slate-500 text-sm mb-8">In-depth analysis of each broker — pros, cons, and who they're best suited for.</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {brokers.map((broker, idx) => (
                <article
                  key={broker.id}
                  id={`review-${broker.id}`}
                  className={`bg-white border rounded-2xl p-6 flex flex-col gap-5 ${broker.id === 'zerodha' ? 'border-navy-300 lg:col-span-2' : 'border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`${broker.color} w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl flex-shrink-0`} aria-hidden="true">{broker.initials}</div>
                      <div>
                        <h3 className="font-serif text-xl font-bold text-slate-900">#{idx + 1} {broker.name}</h3>
                        <StarRating rating={broker.rating} />
                      </div>
                    </div>
                    <div className="bg-navy-50 border border-navy-100 rounded-lg px-4 py-2 text-center">
                      <p className="text-xs text-slate-500 mb-0.5">Key feature</p>
                      <p className="text-navy-600 font-bold text-sm">{broker.keyStat}</p>
                    </div>
                  </div>

                  <p className="text-slate-500 text-sm italic">"{broker.verdict}"</p>

                  <div className={broker.id === 'zerodha' ? 'grid grid-cols-1 md:grid-cols-2 gap-5' : ''}>
                    <div>
                      <h4 className="text-green-600 text-xs font-bold uppercase tracking-widest mb-3">Pros</h4>
                      <ul className="space-y-2">
                        {broker.pros.map((pro) => (
                          <li key={pro} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true">✓</span>
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-3">Cons</h4>
                      <ul className="space-y-2">
                        {broker.cons.map((con) => (
                          <li key={con} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="text-amber-500 mt-0.5 flex-shrink-0" aria-hidden="true">!</span>
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-slate-100">
                    <p className="text-sm text-slate-600">
                      <span className="text-slate-400">Best for:</span>{' '}
                      <span className="text-slate-800 font-medium">{broker.bestFor}</span>
                    </p>
                    <AffiliateButton broker={broker.id} href={broker.affiliateLink} size="sm" />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Decision Guide */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 border-y border-slate-100" aria-labelledby="decision-guide-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="decision-guide-heading" className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Which Demat Account Is Right For You?</h2>
            <p className="text-slate-500 text-sm mb-8">Match your investor profile to the best broker for your needs.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { type: 'Beginner', icon: <Sprout className="w-5 h-5" />, pick: 'Groww', brokerId: 'groww', reason: 'Simplest UI, invest in stocks and mutual funds from one app, ₹0 AMC', color: 'bg-green-600', affiliateLink: brokers.find(b => b.id === 'groww')?.affiliateLink || '#' },
                { type: 'Active Trader', icon: <TrendingUp className="w-5 h-5" />, pick: 'Zerodha', brokerId: 'zerodha', reason: 'Best platform (Kite), largest ecosystem, most trusted broker in India', color: 'bg-blue-600', affiliateLink: brokers.find(b => b.id === 'zerodha')?.affiliateLink || '#' },
                { type: 'Options Trader', icon: <Zap className="w-5 h-5" />, pick: 'Fyers', brokerId: 'fyers', reason: 'Best Options Hub analytics, fast execution, TradingView integration', color: 'bg-red-600', affiliateLink: brokers.find(b => b.id === 'fyers')?.affiliateLink || '#' },
                { type: 'Long-term Investor', icon: <Landmark className="w-5 h-5" />, pick: 'Upstox', brokerId: 'upstox', reason: 'Low AMC, zero delivery brokerage, clean mobile interface', color: 'bg-purple-600', affiliateLink: brokers.find(b => b.id === 'upstox')?.affiliateLink || '#' },
                { type: 'Research-Focused', icon: <Microscope className="w-5 h-5" />, pick: 'Angel One', brokerId: 'angelone', reason: 'ARQ Prime AI recommendations, SmartAPI for custom algo strategies', color: 'bg-orange-600', affiliateLink: brokers.find(b => b.id === 'angelone')?.affiliateLink || '#' },
                { type: 'SIP & Mutual Funds', icon: <PiggyBank className="w-5 h-5" />, pick: 'Groww', brokerId: 'groww', reason: 'Best mutual fund discovery, SIP tracking, and goal-based investing UI', color: 'bg-green-600', affiliateLink: brokers.find(b => b.id === 'groww')?.affiliateLink || '#' },
              ].map(({ type, icon, pick, brokerId, reason, color, affiliateLink }) => (
                <div key={`${type}-${pick}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 hover:border-navy-200 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-navy-500 flex-shrink-0">{icon}</span>
                    <div>
                      <p className="text-slate-400 text-xs uppercase tracking-widest">If you are a</p>
                      <p className="text-slate-900 font-bold text-base">{type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`${color} w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0`} aria-hidden="true">{pick[0]}</div>
                    <p className="text-navy-600 font-bold text-sm">Choose {pick}</p>
                  </div>
                  <p className="text-slate-500 text-xs leading-relaxed">{reason}</p>
                  <a href={affiliateLink} data-broker={brokerId} target="_blank" rel="noopener noreferrer sponsored"
                    className="text-xs text-navy-500 hover:text-navy-700 transition-colors underline underline-offset-2 self-start">
                    Open {pick} account &rarr;
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-heading" className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
            <p className="text-slate-500 text-sm mb-8">Everything you need to know about demat accounts in India.</p>
            <div className="space-y-3">
              {faqs.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} index={i} />)}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-14 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-100" aria-labelledby="cta-heading">
          <div className="max-w-7xl mx-auto text-center">
            <h2 id="cta-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3">Ready to Start Investing?</h2>
            <p className="text-slate-600 text-lg mb-10">Open your free demat account today — takes less than 15 minutes online.</p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
              {brokers.map((broker) => (
                <div key={broker.id} className="flex flex-col items-center gap-2">
                  <div className={`${broker.color} w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm`} aria-hidden="true">{broker.initials}</div>
                  <a
                    href={broker.affiliateLink}
                    data-broker={broker.id}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="inline-flex items-center justify-center font-bold rounded-xl transition-colors duration-150 bg-navy-500 hover:bg-navy-600 text-white px-5 py-2.5 text-sm"
                    aria-label={`Open free ${broker.name} demat account`}
                  >
                    {broker.name} &rarr;
                  </a>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm mb-8">
              <Link to="/blog/understanding-stock-market-basics-complete-beginners-guide" className="text-navy-500 hover:text-navy-700 underline underline-offset-2 transition-colors">Read our stock market basics guide</Link>
              <Link to="/ask" className="text-navy-500 hover:text-navy-700 underline underline-offset-2 transition-colors">Try our free AI stock analysis</Link>
            </div>

            <p className="text-xs text-slate-400 max-w-2xl mx-auto leading-relaxed">
              <strong className="text-slate-600">Disclaimer:</strong> IndianStockMarketGPT is not a SEBI-registered investment adviser. This page is educational comparison content only and does not constitute financial or investment advice. Please read all scheme-related documents carefully before investing. Past performance is not indicative of future returns.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default DematPage;
